import React, { useState, useEffect, useCallback, useMemo } from 'react'
import api from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import { COMPASS_16 } from '../utils/windDirections'
import './SiteAdmin.css'

function SiteAdmin() {
  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [editingSite, setEditingSite] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [activeView, setActiveView] = useState('list') // 'list', 'analytics', 'export'

  // Filtering and search state
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [regionFilter, setRegionFilter] = useState('')
  const [publishedFilter, setPublishedFilter] = useState('')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')

  // Analytics state
  const [analytics, setAnalytics] = useState({
    totalSites: 0,
    statusDistribution: {},
    regionDistribution: {},
    popularSites: [],
    recentActivity: []
  })
  const [formData, setFormData] = useState({
    name: '',
    direction: '',
    directions: [],
    height: '',
    height_meters: '',
    latitude: '',
    longitude: '',
    description: '',
    access: '',
    parking: '',
    takeoff: '',
    landing: '',
    hazards: '',
    other: '',
    status: 'Open',
    region: '',
    os_map: '',
    grid_ref: '',
    parking_latitude: '',
    parking_longitude: '',
    landing_latitude: '',
    landing_longitude: '',
    lambing: false,
    lambing_notes: '',
    restricted_area: '',
    published: true,
    // Additional fields from CSV
    bestwinddirection: '',
    t2b: '',
    takeoffgrid: '',
    landinggrid: '',
    parkinggrid: '',
    takeoff_w3w: '',
    landing_w3w: '',
    parking_w3w: '',
    // New admin fields
    access_level: 'public',
    seasonal_restrictions: [],
    special_conditions: []
  })
  const [bulkAction, setBulkAction] = useState('')
  const [selectedSites, setSelectedSites] = useState(new Set())
  const [submitLoading, setSubmitLoading] = useState(false)

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    type: 'warning' // 'warning', 'danger'
  })

  const loadSites = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const sitesData = await api.sites.getAllSites()
      setSites(sitesData)
    } catch (err) {
      setError(err.message)
      console.error('Error loading sites:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadAnalytics = useCallback(async () => {
    try {
      // Calculate analytics from sites data
      // In a real implementation, this would call an analytics API endpoint
      const statusDistribution = {}
      const regionDistribution = {}

      sites.forEach(site => {
        // Status distribution
        const status = site.status || 'Unknown'
        statusDistribution[status] = (statusDistribution[status] || 0) + 1

        // Region distribution
        const region = site.region || 'Unknown'
        regionDistribution[region] = (regionDistribution[region] || 0) + 1
      })

      setAnalytics({
        totalSites: sites.length,
        statusDistribution,
        regionDistribution,
        popularSites: sites.slice(0, 10), // Mock popular sites
        recentActivity: [] // Mock recent activity
      })
    } catch (err) {
      console.error('Error loading analytics:', err)
    }
  }, [sites])

  useEffect(() => {
    // On mount `loading` already starts true, so drive the fetch here rather
    // than calling loadSites() synchronously (which would set state within the
    // effect). loadSites remains for imperative refreshes after mutations.
    let ignore = false
    ;(async () => {
      try {
        const sitesData = await api.sites.getAllSites()
        if (ignore) return
        setSites(sitesData)
      } catch (err) {
        if (!ignore) {
          setError(err.message)
          console.error('Error loading sites:', err)
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [])

  // filteredSites is derived from the sites + the active filters/sort. Computing
  // it during render (useMemo) avoids mirroring it into state via an effect.
  const filteredSites = useMemo(() => {
    let filtered = [...sites]

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(site =>
        site.name?.toLowerCase().includes(term) ||
        site.region?.toLowerCase().includes(term) ||
        site.description?.toLowerCase().includes(term)
      )
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(site => site.status === statusFilter)
    }

    // Apply region filter
    if (regionFilter) {
      filtered = filtered.filter(site => site.region === regionFilter)
    }

    // Apply published filter
    if (publishedFilter) {
      const isPublished = publishedFilter === 'published'
      filtered = filtered.filter(site =>
        isPublished ? (site.published !== false && site.published !== '0') : (site.published === false || site.published === '0')
      )
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy] || ''
      let bVal = b[sortBy] || ''

      // Handle numeric sorting for height
      if (sortBy === 'height_meters') {
        aVal = parseInt(aVal) || 0
        bVal = parseInt(bVal) || 0
      } else {
        aVal = aVal.toString().toLowerCase()
        bVal = bVal.toString().toLowerCase()
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
      }
    })

    return filtered
  }, [sites, searchTerm, statusFilter, regionFilter, publishedFilter, sortBy, sortOrder])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  // Toggle a compass direction in the structured directions[] array.
  const toggleDirection = (dir) => {
    setFormData(prev => {
      const set = new Set(prev.directions || [])
      if (set.has(dir)) set.delete(dir); else set.add(dir)
      // keep in compass order
      const ordered = COMPASS_16.filter(d => set.has(d))
      return { ...prev, directions: ordered }
    })
  }

  const resetForm = () => {
    setFormData({
      name: '',
      direction: '',
      directions: [],
      height: '',
      height_meters: '',
      latitude: '',
      longitude: '',
      description: '',
      access: '',
      parking: '',
      takeoff: '',
      landing: '',
      hazards: '',
      other: '',
      status: 'Open',
      region: '',
      os_map: '',
      grid_ref: '',
      parking_latitude: '',
      parking_longitude: '',
      landing_latitude: '',
      landing_longitude: '',
      lambing: false,
      lambing_notes: '',
      restricted_area: '',
      published: true,
      bestwinddirection: '',
      t2b: '',
      takeoffgrid: '',
      landinggrid: '',
      parkinggrid: '',
      takeoff_w3w: '',
      landing_w3w: '',
      parking_w3w: '',
      access_level: 'public',
      seasonal_restrictions: [],
      special_conditions: []
    })
    setEditingSite(null)
    setShowCreateForm(false)
    setError(null)
    setSuccess(null)
  }

  const showConfirmationDialog = (title, message, onConfirm, type = 'warning') => {
    setConfirmDialog({
      show: true,
      title,
      message,
      onConfirm,
      onCancel: () => setConfirmDialog({ ...confirmDialog, show: false }),
      type
    })
  }

  const handleBulkAction = async () => {
    if (!bulkAction || selectedSites.size === 0) return

    const action = bulkAction
    const siteIds = Array.from(selectedSites)
    const actionText = {
      'open': 'set to Open',
      'close': 'set to Closed',
      'restrict': 'set to Restricted',
      'publish': 'publish',
      'unpublish': 'unpublish',
      'delete': 'delete',
      'lambing_on': 'enable lambing restrictions for',
      'lambing_off': 'disable lambing restrictions for'
    }[action] || action

    const dialogType = action === 'delete' ? 'danger' : 'warning'

    showConfirmationDialog(
      `Bulk ${action.charAt(0).toUpperCase() + action.slice(1)} Sites`,
      `Are you sure you want to ${actionText} ${siteIds.length} site(s)? This action cannot be undone.`,
      async () => {
        try {
          setError(null)
          setSuccess(null)

          for (const siteId of siteIds) {
            const site = sites.find(s => s.site_id === siteId)
            if (!site) continue

            let updateData = {}

            switch (action) {
              case 'open':
                updateData = { status: 'Open' }
                break
              case 'close':
                updateData = { status: 'Closed' }
                break
              case 'restrict':
                updateData = { status: 'Restricted' }
                break
              case 'publish':
                updateData = { published: true }
                break
              case 'unpublish':
                updateData = { published: false }
                break
              case 'lambing_on':
                updateData = { lambing: true }
                break
              case 'lambing_off':
                updateData = { lambing: false }
                break
              case 'delete':
                await api.sites.deleteSite(siteId)
                continue
              default:
                continue
            }

            if (Object.keys(updateData).length > 0) {
              await api.sites.updateSite(siteId, updateData)
            }
          }

          await loadSites()
          await loadAnalytics()
          setSelectedSites(new Set())
          setBulkAction('')
          setSuccess(`Successfully ${actionText} ${siteIds.length} site(s)`)

          // Clear success message after 5 seconds
          setTimeout(() => setSuccess(null), 5000)

        } catch (err) {
          setError(err.message)
          console.error('Error performing bulk action:', err)
        }
      },
      dialogType
    )
  }

  const handleSiteSelection = (siteId, checked) => {
    const newSelection = new Set(selectedSites)
    if (checked) {
      newSelection.add(siteId)
    } else {
      newSelection.delete(siteId)
    }
    setSelectedSites(newSelection)
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSites(new Set(sites.map(site => site.site_id)))
    } else {
      setSelectedSites(new Set())
    }
  }

  const handleEdit = (site) => {
    setFormData({
      name: site.name || '',
      direction: site.direction || '',
      directions: Array.isArray(site.directions) ? site.directions : [],
      height: site.height || '',
      height_meters: site.height_meters || '',
      latitude: site.latitude || '',
      longitude: site.longitude || '',
      description: site.description || '',
      access: site.access || '',
      parking: site.parking || '',
      takeoff: site.takeoff || '',
      landing: site.landing || '',
      hazards: site.hazards || '',
      other: site.other || '',
      status: site.status || 'Open',
      region: site.region || '',
      os_map: site.os_map || '',
      grid_ref: site.grid_ref || '',
      parking_latitude: site.parking_latitude || '',
      parking_longitude: site.parking_longitude || '',
      landing_latitude: site.landing_latitude || '',
      landing_longitude: site.landing_longitude || '',
      lambing: site.lambing || false,
      lambing_notes: site.lambing_notes || '',
      restricted_area: site.restricted_area || '',
      published: site.published !== false && site.published !== '0',
      bestwinddirection: site.bestwinddirection || '',
      t2b: site.t2b || '',
      takeoffgrid: site.takeoffgrid || '',
      landinggrid: site.landinggrid || '',
      parkinggrid: site.parkinggrid || '',
      takeoff_w3w: site.takeoff_w3w || '',
      landing_w3w: site.landing_w3w || '',
      parking_w3w: site.parking_w3w || '',
      access_level: site.access_level || 'public',
      seasonal_restrictions: site.seasonal_restrictions || [],
      special_conditions: site.special_conditions || []
    })
    setEditingSite(site)
    setShowCreateForm(true)
    setError(null)
    setSuccess(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setSubmitLoading(true)
      setError(null)
      setSuccess(null)

      // Convert string numbers to appropriate types
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        height_meters: formData.height_meters ? parseInt(formData.height_meters) : null,
        parking_latitude: formData.parking_latitude ? parseFloat(formData.parking_latitude) : null,
        parking_longitude: formData.parking_longitude ? parseFloat(formData.parking_longitude) : null,
        landing_latitude: formData.landing_latitude ? parseFloat(formData.landing_latitude) : null,
        landing_longitude: formData.landing_longitude ? parseFloat(formData.landing_longitude) : null,
      }

      if (editingSite) {
        await api.sites.updateSite(editingSite.site_id, submitData)
        setSuccess(`Successfully updated site "${formData.name}"`)
      } else {
        await api.sites.createSite(submitData)
        setSuccess(`Successfully created site "${formData.name}"`)
      }

      await loadSites()
      await loadAnalytics()
      resetForm()

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000)
    } catch (err) {
      setError(err.message)
      console.error('Error saving site:', err)
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleDelete = async (siteId, siteName) => {
    showConfirmationDialog(
      'Delete Site',
      `Are you sure you want to delete "${siteName}"? This action cannot be undone and will remove all associated data.`,
      async () => {
        try {
          setError(null)
          await api.sites.deleteSite(siteId)
          await loadSites()
          await loadAnalytics()
          setSuccess(`Successfully deleted site "${siteName}"`)
          setTimeout(() => setSuccess(null), 5000)
        } catch (err) {
          setError(err.message)
          console.error('Error deleting site:', err)
        }
      },
      'danger'
    )
  }

  const handleExportData = () => {
    try {
      const exportData = filteredSites.map(site => ({
        name: site.name,
        direction: site.direction,
        height_meters: site.height_meters,
        latitude: site.latitude,
        longitude: site.longitude,
        status: site.status,
        region: site.region,
        published: site.published,
        description: site.description,
        access: site.access,
        lambing: site.lambing,
        restricted_area: site.restricted_area
      }))

      const csv = [
        // Header row
        Object.keys(exportData[0]).join(','),
        // Data rows
        ...exportData.map(row =>
          Object.values(row).map(value =>
            typeof value === 'string' && value.includes(',')
              ? `"${value.replace(/"/g, '""')}"`
              : value
          ).join(',')
        )
      ].join('\n')

      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `wingbeat-sites-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      setSuccess('Site data exported successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError('Failed to export data: ' + err.message)
    }
  }

  const toggleSiteStatus = async (siteId, currentStatus) => {
    const newStatus = currentStatus === 'Open' ? 'Closed' : 'Open'

    try {
      setError(null)
      // Try admin API first, fallback to sites API
      try {
        await api.admin.updateSiteStatus(siteId, newStatus)
      } catch {
        await api.sites.updateSite(siteId, { status: newStatus })
      }
      await loadSites()
      await loadAnalytics()
      setSuccess(`Site status updated to ${newStatus}`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.message)
      console.error('Error updating site status:', err)
    }
  }

  const toggleSitePublished = async (siteId, currentPublished) => {
    const newPublished = !currentPublished

    try {
      setError(null)
      // Try admin API first, fallback to sites API
      try {
        await api.admin.updateSitePublishing(siteId, newPublished)
      } catch {
        await api.sites.updateSite(siteId, { published: newPublished })
      }
      await loadSites()
      await loadAnalytics()
      setSuccess(`Site ${newPublished ? 'published' : 'unpublished'} successfully`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.message)
      console.error('Error updating site visibility:', err)
    }
  }

  if (loading) {
    return <LoadingSpinner size="large" message="Loading sites..." />
  }

  // Get unique values for filters
  const uniqueStatuses = [...new Set(sites.map(site => site.status).filter(Boolean))]
  const uniqueRegions = [...new Set(sites.map(site => site.region).filter(Boolean))]

  return (
    <div className="site-admin">
      {/* Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="modal-overlay">
          <div className={`confirmation-dialog ${confirmDialog.type}`}>
            <div className="dialog-header">
              <h3>{confirmDialog.title}</h3>
            </div>
            <div className="dialog-body">
              <p>{confirmDialog.message}</p>
            </div>
            <div className="dialog-actions">
              <button
                onClick={confirmDialog.onCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmDialog.onConfirm()
                  setConfirmDialog({ ...confirmDialog, show: false })
                }}
                className={`btn ${confirmDialog.type === 'danger' ? 'btn-danger' : 'btn-primary'}`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with Navigation */}
      <div className="admin-header">
        <div className="header-content">
          <h2>Sites Administration</h2>
          <div className="header-stats">
            <span className="stat-item">
              <strong>{filteredSites.length}</strong> of <strong>{sites.length}</strong> sites
            </span>
          </div>
        </div>

        <div className="admin-nav">
          <button
            className={`nav-btn ${activeView === 'list' ? 'active' : ''}`}
            onClick={() => setActiveView('list')}
          >
            📋 Sites List
          </button>
          <button
            className={`nav-btn ${activeView === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveView('analytics')}
          >
            📊 Analytics
          </button>
          <button
            className={`nav-btn ${activeView === 'export' ? 'active' : ''}`}
            onClick={() => setActiveView('export')}
          >
            📤 Export
          </button>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span className="alert-message">{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          <span className="alert-message">{success}</span>
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Main Content */}
      <div className="admin-content">
        {activeView === 'list' && (
          <>
            {/* Action Bar */}
            <div className="action-bar">
              <div className="primary-actions">
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="btn btn-primary"
                  disabled={showCreateForm}
                >
                  ➕ Add New Site
                </button>

                <button
                  onClick={handleExportData}
                  className="btn btn-secondary"
                  disabled={filteredSites.length === 0}
                >
                  📤 Export Data
                </button>
              </div>

              {selectedSites.size > 0 && (
                <div className="bulk-actions">
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className="bulk-select"
                  >
                    <option value="">Bulk Actions ({selectedSites.size} selected)</option>
                    <option value="open">Set to Open</option>
                    <option value="close">Set to Closed</option>
                    <option value="restrict">Set to Restricted</option>
                    <option value="publish">Publish Sites</option>
                    <option value="unpublish">Unpublish Sites</option>
                    <option value="lambing_on">Enable Lambing Restrictions</option>
                    <option value="lambing_off">Disable Lambing Restrictions</option>
                    <option value="delete">Delete Sites</option>
                  </select>
                  <button
                    onClick={handleBulkAction}
                    className="btn btn-secondary"
                    disabled={!bulkAction}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="filters-section">
              <div className="filters-grid">
                <div className="filter-group">
                  <label htmlFor="search">Search Sites</label>
                  <input
                    type="text"
                    id="search"
                    placeholder="Search by name, region, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="statusFilter">Status</label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    {uniqueStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="regionFilter">Region</label>
                  <select
                    id="regionFilter"
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                  >
                    <option value="">All Regions</option>
                    {uniqueRegions.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="publishedFilter">Visibility</label>
                  <select
                    id="publishedFilter"
                    value={publishedFilter}
                    onChange={(e) => setPublishedFilter(e.target.value)}
                  >
                    <option value="">All Sites</option>
                    <option value="published">Published Only</option>
                    <option value="unpublished">Unpublished Only</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="sortBy">Sort By</label>
                  <select
                    id="sortBy"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Name</option>
                    <option value="height_meters">Height</option>
                    <option value="status">Status</option>
                    <option value="region">Region</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="sortOrder">Order</label>
                  <select
                    id="sortOrder"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>

              {(searchTerm || statusFilter || regionFilter || publishedFilter) && (
                <button
                  onClick={() => {
                    setSearchTerm('')
                    setStatusFilter('')
                    setRegionFilter('')
                    setPublishedFilter('')
                  }}
                  className="btn btn-secondary clear-filters"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </>
        )}

        {activeView === 'analytics' && (
          <div className="analytics-section">
            <h3>Site Analytics Dashboard</h3>

            <div className="analytics-grid">
              <div className="analytics-card">
                <h4>Status Distribution</h4>
                <div className="status-stats">
                  {Object.entries(analytics.statusDistribution).map(([status, count]) => (
                    <div key={status} className="stat-row">
                      <span className={`status-indicator ${status.toLowerCase()}`}></span>
                      <span className="stat-label">{status}</span>
                      <span className="stat-value">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="analytics-card">
                <h4>Regional Distribution</h4>
                <div className="region-stats">
                  {Object.entries(analytics.regionDistribution).map(([region, count]) => (
                    <div key={region} className="stat-row">
                      <span className="stat-label">{region}</span>
                      <span className="stat-value">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="analytics-card">
                <h4>Quick Stats</h4>
                <div className="quick-stats">
                  <div className="stat-item">
                    <span className="stat-number">{analytics.totalSites}</span>
                    <span className="stat-description">Total Sites</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {sites.filter(s => s.published !== false && s.published !== '0').length}
                    </span>
                    <span className="stat-description">Published</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {sites.filter(s => s.lambing).length}
                    </span>
                    <span className="stat-description">With Restrictions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeView === 'export' && (
          <div className="export-section">
            <h3>Data Export</h3>
            <div className="export-options">
              <div className="export-card">
                <h4>Export Filtered Sites</h4>
                <p>Export the currently filtered list of {filteredSites.length} sites as CSV.</p>
                <button
                  onClick={handleExportData}
                  className="btn btn-primary"
                  disabled={filteredSites.length === 0}
                >
                  📤 Export CSV
                </button>
              </div>
            </div>
          </div>
        )}

        {showCreateForm && (
          <div className="site-form-container">
            <div className="form-header">
              <h3>{editingSite ? 'Edit Site' : 'Create New Site'}</h3>
              <button onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>

            <form onSubmit={handleSubmit} className="site-form">
              <div className="form-section">
                <h4>Basic Information</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Site Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="direction">Direction notes *</label>
                    <input
                      type="text"
                      id="direction"
                      name="direction"
                      value={formData.direction}
                      onChange={handleInputChange}
                      placeholder="e.g., SSW to SE; 170-210 degs"
                      required
                    />
                    <small className="field-hint">
                      Human-readable notes shown on the site guide. Tick the boxes below to set the
                      exact flyable directions used for filtering.
                    </small>
                  </div>

                  <div className="form-group form-group-full">
                    <label>Flyable wind directions</label>
                    <div className="direction-grid">
                      {COMPASS_16.map(dir => (
                        <label
                          key={dir}
                          className={`direction-chip ${(formData.directions || []).includes(dir) ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={(formData.directions || []).includes(dir)}
                            onChange={() => toggleDirection(dir)}
                          />
                          {dir}
                        </label>
                      ))}
                    </div>
                    <small className="field-hint">
                      {(formData.directions || []).length > 0
                        ? `Selected: ${formData.directions.join(', ')}`
                        : 'No directions tagged yet.'}
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="height">Height Display</label>
                    <input
                      type="text"
                      id="height"
                      name="height"
                      value={formData.height}
                      onChange={handleInputChange}
                      placeholder="e.g., 1440ft, 440m"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="height_meters">Height (meters) *</label>
                    <input
                      type="number"
                      id="height_meters"
                      name="height_meters"
                      value={formData.height_meters}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                      <option value="Restricted">Restricted</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="region">Region</label>
                    <input
                      type="text"
                      id="region"
                      name="region"
                      value={formData.region}
                      onChange={handleInputChange}
                      placeholder="e.g., Scottish Borders"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Location</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="latitude">Latitude *</label>
                    <input
                      type="number"
                      step="any"
                      id="latitude"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      placeholder="55.1234"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="longitude">Longitude *</label>
                    <input
                      type="number"
                      step="any"
                      id="longitude"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      placeholder="-2.5678"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="grid_ref">Grid Reference</label>
                    <input
                      type="text"
                      id="grid_ref"
                      name="grid_ref"
                      value={formData.grid_ref}
                      onChange={handleInputChange}
                      placeholder="NT 123 456"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="os_map">OS Map</label>
                    <input
                      type="text"
                      id="os_map"
                      name="os_map"
                      value={formData.os_map}
                      onChange={handleInputChange}
                      placeholder="Explorer 338"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Additional Coordinates</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="parking_latitude">Parking Latitude</label>
                    <input
                      type="number"
                      step="any"
                      id="parking_latitude"
                      name="parking_latitude"
                      value={formData.parking_latitude}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="parking_longitude">Parking Longitude</label>
                    <input
                      type="number"
                      step="any"
                      id="parking_longitude"
                      name="parking_longitude"
                      value={formData.parking_longitude}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="landing_latitude">Landing Latitude</label>
                    <input
                      type="number"
                      step="any"
                      id="landing_latitude"
                      name="landing_latitude"
                      value={formData.landing_latitude}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="landing_longitude">Landing Longitude</label>
                    <input
                      type="number"
                      step="any"
                      id="landing_longitude"
                      name="landing_longitude"
                      value={formData.landing_longitude}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Detailed Information</h4>
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="access">Access</label>
                  <textarea
                    id="access"
                    name="access"
                    value={formData.access}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="parking">Parking</label>
                  <textarea
                    id="parking"
                    name="parking"
                    value={formData.parking}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="takeoff">Takeoff</label>
                  <textarea
                    id="takeoff"
                    name="takeoff"
                    value={formData.takeoff}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="landing">Landing</label>
                  <textarea
                    id="landing"
                    name="landing"
                    value={formData.landing}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="hazards">Hazards</label>
                  <textarea
                    id="hazards"
                    name="hazards"
                    value={formData.hazards}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="other">Other Information</label>
                  <textarea
                    id="other"
                    name="other"
                    value={formData.other}
                    onChange={handleInputChange}
                    rows="2"
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Status & Visibility</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="status">Site Status</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Open">🟢 Open</option>
                      <option value="Restricted">🟡 Restricted</option>
                      <option value="Closed">🔴 Closed</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="access_level">Access Level</label>
                    <select
                      id="access_level"
                      name="access_level"
                      value={formData.access_level}
                      onChange={handleInputChange}
                    >
                      <option value="public">Public</option>
                      <option value="members">Members Only</option>
                      <option value="restricted">Restricted Access</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="published"
                        checked={formData.published}
                        onChange={handleInputChange}
                      />
                      Published (Visible on public site)
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Restrictions & Conditions</h4>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="lambing"
                      checked={formData.lambing}
                      onChange={handleInputChange}
                    />
                    Lambing Restrictions Apply
                  </label>
                </div>

                {formData.lambing && (
                  <div className="form-group">
                    <label htmlFor="lambing_notes">Lambing Notes</label>
                    <textarea
                      id="lambing_notes"
                      name="lambing_notes"
                      value={formData.lambing_notes}
                      onChange={handleInputChange}
                      rows="2"
                      placeholder="Details about lambing restrictions..."
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="restricted_area">Restricted Area Details</label>
                  <textarea
                    id="restricted_area"
                    name="restricted_area"
                    value={formData.restricted_area}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Details about any restricted areas..."
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editingSite ? 'Update Site' : 'Create Site')}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeView === 'list' && !showCreateForm && (
          <div className="sites-table-container">
            <div className="table-header">
              <h3>Sites List</h3>
              <div className="table-controls">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={selectedSites.size === filteredSites.length && filteredSites.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  Select All
                </label>
              </div>
            </div>

            {filteredSites.length === 0 ? (
              <div className="empty-state">
                <h4>No sites found</h4>
                <p>
                  {sites.length === 0
                    ? "No sites have been created yet. Click 'Add New Site' to get started."
                    : "No sites match your current filters. Try adjusting your search criteria."
                  }
                </p>
              </div>
            ) : (
              <div className="sites-table-wrapper">
                <table className="sites-table">
                  <thead>
                    <tr>
                      <th className="select-column">
                        <input
                          type="checkbox"
                          checked={selectedSites.size === filteredSites.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th>Site Name</th>
                      <th>Status</th>
                      <th>Published</th>
                      <th>Direction</th>
                      <th>Height</th>
                      <th>Region</th>
                      <th>Restrictions</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSites.map(site => (
                      <tr key={site.site_id} className={selectedSites.has(site.site_id) ? 'selected' : ''}>
                        <td className="select-column">
                          <input
                            type="checkbox"
                            checked={selectedSites.has(site.site_id)}
                            onChange={(e) => handleSiteSelection(site.site_id, e.target.checked)}
                          />
                        </td>
                        <td className="site-name">
                          <strong>{site.name}</strong>
                          {site.description && (
                            <div className="site-description">
                              {site.description.substring(0, 100)}
                              {site.description.length > 100 ? '...' : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          <button
                            className={`status-badge ${site.status?.toLowerCase() || 'unknown'} clickable`}
                            onClick={() => toggleSiteStatus(site.site_id, site.status)}
                            title="Click to toggle status"
                          >
                            {site.status === 'Open' && '🟢'}
                            {site.status === 'Restricted' && '🟡'}
                            {site.status === 'Closed' && '🔴'}
                            {site.status || 'Unknown'}
                          </button>
                        </td>
                        <td>
                          <button
                            className={`published-badge ${(site.published !== false && site.published !== '0') ? 'published' : 'unpublished'} clickable`}
                            onClick={() => toggleSitePublished(site.site_id, site.published !== false && site.published !== '0')}
                            title="Click to toggle visibility"
                          >
                            {(site.published !== false && site.published !== '0') ? '✅ Published' : '❌ Hidden'}
                          </button>
                        </td>
                        <td>{site.direction || 'N/A'}</td>
                        <td>{site.height_meters ? `${site.height_meters}m` : 'N/A'}</td>
                        <td>{site.region || 'N/A'}</td>
                        <td>
                          <div className="restrictions">
                            {site.lambing && <span className="restriction-tag">🐑 Lambing</span>}
                            {site.restricted_area && <span className="restriction-tag">🚫 Restricted</span>}
                            {!site.lambing && !site.restricted_area && <span className="no-restrictions">None</span>}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => handleEdit(site)}
                              className="btn btn-sm btn-secondary"
                              title="Edit site"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDelete(site.site_id, site.name)}
                              className="btn btn-sm btn-danger"
                              title="Delete site"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SiteAdmin